// #56 Section rail: the left rail of a long form, a loop sequence or the console areas (*Statuses · Reason lists …*).
// Each item is a 40 px link to its section: a 24 px status disc (done = a green check, on = the blue arrow, else an
// empty disc), the section's name and under it its status words. The section in view is .mc-on (blue tint, a
// --blue edge at alpha) and aria-current="step"; a completed one is .mc-done. The status words are the caller's (his
// status chain words), so the state never rests on the disc's colour alone. An item may carry a `hue` (a person-hue
// token): a 10 px dot at the item's end, decorative, beside words that name the person or round (a loop's rounds).

import type { MouseEvent } from "react";
import { Icon } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { vars, type McVarValue } from "../lib/vars";

export type SectionRailState = "done" | "on" | "todo";

export interface SectionRailItem {
  key: string;
  /** The section's heading (his words). */
  title: string;
  /** The status words under it (a word of his chain: *Draft*, *In Progress*, *Finalized* …). */
  status?: string | undefined;
  state?: SectionRailState | undefined;
  /** The section's anchor or screen. */
  href?: string | undefined;
  onSelect?: ((key: string, event: MouseEvent<HTMLElement>) => void) | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  /** A decorative dot at the item's end in this hue (a --p-* token var), e.g. a round's interviewer. */
  hue?: McVarValue | undefined;
}

export interface SectionRailProps {
  /** Names the rail's navigation landmark. */
  label: string;
  items: readonly SectionRailItem[];
  className?: string | undefined;
}

function RailItem({ item }: { item: SectionRailItem }) {
  const hot = useHs(item.hs);
  const state = item.state ?? "todo";
  const inner = (
    <>
      <span className="mc-si" aria-hidden="true">
        {state === "done" ? <Icon name="check" size={14} /> : state === "on" ? <Icon name="fwd" size={14} /> : null}
      </span>
      <span className="mc-secitem-w">
        <span className="mc-secitem-t">{item.title}</span>
        {item.status ? <span className="mc-secitem-st">{item.status}</span> : null}
      </span>
      {item.hue !== undefined ? <span className="mc-secitem-hue" aria-hidden="true" style={vars({ "--mc-hue": item.hue })} /> : null}
    </>
  );
  const props = {
    className: cx("mc-secitem", state !== "todo" && `mc-${state}`),
    "aria-current": state === "on" ? ("step" as const) : undefined,
    ...hot,
  };
  if (item.href === undefined && item.onSelect) {
    return <button type="button" {...props} onClick={(event) => item.onSelect?.(item.key, event)}>{inner}</button>;
  }
  return (
    <a {...props} href={item.href ?? `#${item.key}`} onClick={item.onSelect ? (event) => item.onSelect?.(item.key, event) : undefined}>
      {inner}
    </a>
  );
}

export function SectionRail({ label, items, className }: SectionRailProps) {
  return (
    <nav className={cx("mc-secrail-nav", className)} aria-label={label}>
      <ol className="mc-secrail">
        {items.filter((item) => item.can !== false).map((item) => <li key={item.key}><RailItem item={item} /></li>)}
      </ol>
    </nav>
  );
}
